/**
 * Sección A de la trazabilidad: una fila por etapa del pipeline. Las cinco etapas se pintan
 * siempre, incluso sin datos, para que la ausencia de una etapa sea visible en vez de silenciosa.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PipelineTrace, TraceOutcome, TraceStage, TraceStageName } from '@/api/types';
import { ParticipantDetailTable } from '../shared/ParticipantDetailTable';

const STAGE_LABEL_KEYS: Record<TraceStageName, string> = {
  MeetingEnd: 'trace.stageMeetingEnd',
  MeetingEligibility: 'trace.stageMeetingEligibility',
  ParticipantEligibility: 'trace.stageParticipantEligibility',
  SurveyCreation: 'trace.stageSurveyCreation',
  Notification: 'trace.stageNotification',
};

const OUTCOME_LABEL_KEYS: Record<TraceOutcome, string> = {
  Ok: 'trace.outcomeOk',
  Error: 'trace.outcomeError',
  NotEligible: 'trace.outcomeNotEligible',
  NoData: 'trace.outcomeNoData',
};

const OUTCOME_ICONS: Record<TraceOutcome, string> = {
  Ok: 'fa-check-circle',
  Error: 'fa-times-circle',
  NotEligible: 'fa-ban',
  NoData: 'fa-minus-circle',
};

const STAGE_COL_OUTCOME_KEY = 'trace.stageColOutcome';

function formatTime(timestamp: string | null): string {
  if (!timestamp) return '—';
  return new Date(timestamp).toLocaleString();
}

interface StageRowProps {
  stage: TraceStage;
  trace: PipelineTrace;
}

function StageRow({ stage, trace }: StageRowProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const isParticipantStage = stage.name === 'ParticipantEligibility';

  return (
    <>
      <tr
        className={`trc-stage-row trc-stage-row--${stage.outcome.toLowerCase()}`}
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <td data-label="#">{stage.order}</td>
        <td data-label={t('trace.stageColName')}>{t(STAGE_LABEL_KEYS[stage.name])}</td>
        <td data-label={t('trace.stageColService')}>{stage.service}</td>
        <td data-label={t('trace.stageColTime')}>{formatTime(stage.timestamp)}</td>
        <td data-label={t(STAGE_COL_OUTCOME_KEY)}>
          <span className={`trc-badge trc-badge--${stage.outcome.toLowerCase()}`}>
            <i className={`fas ${OUTCOME_ICONS[stage.outcome]}`} aria-hidden="true" />
            {t(OUTCOME_LABEL_KEYS[stage.outcome])}
          </span>
        </td>
        <td data-label="">
          <i
            className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}
            aria-hidden="true"
          />
        </td>
      </tr>

      {isExpanded && (
        <tr className="trc-stage-detail">
          <td colSpan={6}>
            <p className="trc-stage-summary">{stage.summary}</p>
            {stage.details.length > 0 && (
              <ul className="trc-stage-details">
                {stage.details.map((detail) => <li key={detail}>{detail}</li>)}
              </ul>
            )}
            {isParticipantStage && (
              <ParticipantDetailTable
                rows={trace.participants}
                highlightUserId={trace.highlightUserId}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function TraceStagesTable({ trace }: { trace: PipelineTrace }) {
  const { t } = useTranslation();

  return (
    <section className="trc-stages">
      <h3 className="trc-section-title">{t('trace.stagesTitle')}</h3>
      <div className="trc-table-wrap">
        <table className="trc-table trc-stages-table">
          <thead>
            <tr>
              <th>{t('trace.stageColOrder')}</th>
              <th>{t('trace.stageColName')}</th>
              <th>{t('trace.stageColService')}</th>
              <th>{t('trace.stageColTime')}</th>
              <th>{t(STAGE_COL_OUTCOME_KEY)}</th>
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {trace.stages.map((stage) => (
              <StageRow key={stage.name} stage={stage} trace={trace} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
